import React, { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { ProductSelection, ProductType, ItemType } from 'components/ProductsSelection/types';
import OptionsScrollableList from 'components/OptionsScrollableList';
import { SearchInput } from '@spins/react-ui-library';
import { ScrollableQueryResult } from 'hooks/use-scrollable-query';
import styles from 'components/ProductsSelection/ProductOptions/styles.module.scss';
import { getArrayOfKeys } from 'utils/functions';
import { isEqual, uniqBy } from 'lodash';

export interface ProductOptionsProps {
  queryResult: ScrollableQueryResult<any>;
  totalItems: number;
  currentProductType: ProductType;
  transientSelection: ProductSelection;
  appliedSelection: ProductSelection;
  dataItemMapper(row: any): ItemType;
  onTransientChange(currentProductType: ProductType, value: ItemType): void;
  onTransientSet(currentProductType: ProductType, value: Array<ItemType>): void;
  disabled?: boolean;
  scrollableTarget: string;
  allAvailable?: boolean;
  readonly?: boolean;
  title?: string;
}

const ProductOptions: React.FC<ProductOptionsProps> = ({
  queryResult,
  allAvailable,
  currentProductType,
  appliedSelection,
  transientSelection,
  dataItemMapper,
  onTransientChange,
  onTransientSet,
  totalItems,
  readonly = false,
  title = null,
}) => {
  const selectedItems = transientSelection[currentProductType];
  const appliedSelectedItems = appliedSelection[currentProductType];
  const currentItems = queryResult.data.map(dataItemMapper);

  const allSelected = useMemo(() => {
    return (
      currentItems &&
      currentItems.length > 0 &&
      selectedItems &&
      selectedItems.length > 0 &&
      isEqual(currentItems, selectedItems)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItems, selectedItems]);

  const handleAllChange = (data: Array<any>) => {
    const existItem = data.every(
      (elem) => getArrayOfKeys(selectedItems, 'id').indexOf(elem.id) > -1
    );
    if (existItem) {
      onTransientSet(currentProductType, appliedSelectedItems);
    } else {
      onTransientSet(currentProductType, uniqBy(selectedItems.concat(data), 'id'));
    }
  };

  let helpText = '';

  if (allSelected && selectedItems.length < totalItems) {
    helpText = `(${selectedItems.length} of ${totalItems} Selected: Please scroll to select more)`;
  }

  const handleSearch = (value: string) => {
    queryResult.searching.search(value);
  };

  return (
    <div className={styles['options-list']}>
      <div>{title !== null && <div className={styles['title']}>{title}</div>}</div>
      {!readonly && (
        <div>
          <SearchInput
            key={currentProductType}
            defaultValue={queryResult.searching.value}
            className={styles['search-input']}
            placeholder="Search..."
            onChange={(value) => handleSearch(value)}
          />
        </div>
      )}
      {allAvailable && (
        <div className={styles['select-all-container']}>
          <Button
            className={styles['select-all-btn']}
            onClick={() => handleAllChange(currentItems)}
            disabled={totalItems === appliedSelectedItems.length}
          >
            Add all {currentProductType} - Clear all {currentProductType}
          </Button>
          {helpText !== '' && <span className={styles['help-text']}>{helpText}</span>}
        </div>
      )}
      <div
        key={`infinite-scroll-${currentProductType}`}
        className={styles['infinite-scroll']}
        id="infinite-scroll-content-id"
      >
        <OptionsScrollableList
          currentItems={currentItems}
          queryResult={queryResult}
          scrollableTarget="infinite-scroll-content-id"
          selectedItemsIds={getArrayOfKeys(transientSelection[currentProductType], 'id')}
          setSelectedItem={(item: ItemType) => {
            onTransientChange(currentProductType, item);
          }}
          totalItems={totalItems}
          allSelected={allSelected}
        />
      </div>
    </div>
  );
};

export default ProductOptions;
